import { useMutation, useQuery } from 'react-query';
import { stringifyUrl } from 'query-string';
import { fetcher, FetchError } from 'barnyard/typescript/utils/fetcher';

import {
  EmailUpdateFormValues,
  EmailUpdateFormResponse,
  ProfileResponse,
  ProfileUpdateFormValues,
  ProfileVerifyParams,
  ProfileVerifyResponse,
  SuspiciousLoginParams,
  SuspiciousLoginValues,
  SuspiciousLoginResponse,
  PasswordResetValues,
  PasswordResetResponse,
  PasswordUpdateFormValues,
  PasswordSetValues,
  PasswordResetQueryParams,
  PasswordSetResponse,
} from '../types';

const getProfileKey = () => '/api/accounts/sso/profile/';
const getPasswordUpdateKey = () => '/api/accounts/sso/password/';
const getEmailUpdateKey = () => '/api/accounts/sso/email/';
const getProfileVerifyKey = () => '/api/accounts/profile/verify/';
const getSuspiciousLoginKey = (query: Partial<SuspiciousLoginParams>) => {
  const url = '/api/accounts/suspicious-login/';
  return stringifyUrl({ url, query: query || {} });
};
const getPasswordResetKey = () => '/api/accounts/password/reset/';

export const useProfileQuery = () => {
  return useQuery([getProfileKey()], (url: string) => fetcher<ProfileResponse>({ url }));
};

export const useProfileMutation = ({
  onSuccess,
}: {
  onSuccess: (response: ProfileResponse) => void;
}) => {
  const url = getProfileKey();
  return useMutation<ProfileResponse, FetchError, Partial<ProfileUpdateFormValues>>(
    (body) => fetcher({ url, body, method: 'PUT' }),
    {
      onSuccess,
    }
  );
};

export const usePasswordMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: FetchError) => void;
}) => {
  const url = getPasswordUpdateKey();
  return useMutation<void, FetchError, PasswordUpdateFormValues>(
    (body) => fetcher({ url, body, method: 'PUT' }),
    {
      onSuccess,
      onError,
    }
  );
};

export const useEmailMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: EmailUpdateFormResponse) => void;
  onError: (error: FetchError) => void;
}) => {
  const url = getEmailUpdateKey();
  return useMutation<EmailUpdateFormResponse, FetchError, EmailUpdateFormValues>(
    (body) => fetcher({ url, body, method: 'PUT' }),
    {
      onSuccess,
      onError,
    }
  );
};

export const useProfileVerifyQuery = (params: ProfileVerifyParams) => {
  return useQuery<ProfileVerifyResponse, FetchError>(
    [getProfileVerifyKey(), params],
    (url: string, body: ProfileVerifyParams) => fetcher<ProfileVerifyResponse>({ url, body })
  );
};

export const useSuspiciousLoginQuery = (params: SuspiciousLoginParams) => {
  return useQuery<SuspiciousLoginResponse, FetchError>(
    [getSuspiciousLoginKey({}), params],
    (url: string, body: SuspiciousLoginParams) => fetcher<SuspiciousLoginResponse>({ url, body })
  );
};

export const useSuspiciousLoginMutation = (
  params: SuspiciousLoginParams,
  {
    onSuccess,
    onError,
  }: {
    onSuccess: (response: SuspiciousLoginResponse) => void;
    onError: (error: FetchError) => void;
  }
) => {
  const url = getSuspiciousLoginKey(params);
  return useMutation<SuspiciousLoginResponse, FetchError, SuspiciousLoginValues>(
    (body) => fetcher({ url, body, method: 'PUT' }),
    {
      onSuccess,
      onError,
    }
  );
};

export const usePasswordResetQuery = (params: PasswordResetQueryParams) => {
  return useQuery<PasswordResetResponse, FetchError>(
    [getPasswordResetKey(), params],
    (url: string, body: PasswordResetQueryParams) => fetcher<PasswordResetResponse>({ url, body })
  );
};

export const usePasswordResetMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: PasswordResetResponse) => void;
  onError: (error: FetchError) => void;
}) => {
  const url = getPasswordResetKey();
  return useMutation<PasswordResetResponse, FetchError, PasswordResetValues>(
    (body) => fetcher({ url, body, method: 'POST' }),
    {
      onSuccess,
      onError,
    }
  );
};

export const usePasswordSetMutation = ({
  onSuccess,
}: {
  onSuccess: (response: PasswordSetResponse) => void;
}) => {
  const url = getPasswordResetKey();
  return useMutation<PasswordSetResponse, FetchError, PasswordSetValues>(
    (body) => fetcher({ url, body, method: 'PUT' }),
    {
      onSuccess,
    }
  );
};
